// ---------------------------------------------------------------------------
// What the campaign page loads and polls on its own (R16b)
//
// Until R16b these were eight functional-lit effects in
// pages/campaign-detail.ts. Each ran on a timer one turn after the render that
// changed what it depended on, and its cleanup ran in that render. The moments
// are kept exactly (tests/dom/campaign-polling.dom.ts holds them on a fake
// clock):
//
//   - When the page opens, one timer turn after its first render: the five
//     campaign loads (campaign, leads, messages, LinkedIn jobs, sending
//     status), the projects, the Sales Reps' names, the visibility listener
//     and the reply drafts, in that order.
//   - (1) Status 'generating': the five every 3 s.
//   - (2) The tab visible again: the five at once, whatever the status.
//   - (3) Status 'preparing_to_launch' with a launch time: the countdown at
//     once and every second, the five every 10 s; both start over when the
//     launch time moves.
//   - (4) Status 'active': a silent sync every 60 s, skipped while the tab is
//     hidden (the page's sync itself never runs on top of another).
//   - Each of (1), (3) and (4) starts one timer turn after the render that
//     shows its phase, and stops in the render that shows another. All of
//     them stop when the page is left, (2) with them.
//   - (5) After "Launch Now" when the server resumes the campaign:
//     waitForLaunch(), the campaign alone every 2 s, up to 60 times, until its
//     status is no longer 'preparing_to_launch'. It belongs to the click, not
//     to the page, and runs to its end as it always did.
//
// The page hands its loaders to CampaignLoops in every render (use()), and the
// loops call the latest ones. Plain TypeScript; the controller has Lit's
// ReactiveController shape and needs nothing from Lit.
// ---------------------------------------------------------------------------

/** Lit's ReactiveController, by shape. */
interface Controller {
  hostConnected?(): void;
  hostUpdated?(): void;
  hostDisconnected?(): void;
}

/** What a controller is added to (PageHost). */
interface Host {
  addController(controller: Controller): void;
}

/** What the page hands over in each render: its loaders, and the phase it shows. */
export interface CampaignLoopInput {
  /** The five campaign loads. */
  reload(): void;
  /** The loads that run once when the page opens, beside the five, in order: projects, Sales Reps. */
  openLoads: readonly (() => void)[];
  /** The reply drafts, the last load when the page opens. */
  drafts(): void;
  /** A sync that shows nothing (no toast, no spinner). */
  sync(): void;
  /** Writes the cool-down's remaining time. */
  countdown(remainingMs: number): void;
  /** The campaign's status, when it is loaded. */
  status: string | undefined;
  /** When the cool-down ends, while the campaign is in it. */
  scheduledLaunchAt: string | undefined;
}

export const GENERATING_POLL_MS = 3000;
export const COUNTDOWN_TICK_MS = 1000;
export const COOL_DOWN_RELOAD_MS = 10_000;
/**
 * How often the campaign-detail page silently auto-syncs while the campaign is
 * active and the tab is visible. Runs in addition to the server-side
 * sync-tick-worker (which keeps campaigns fresh when no tab is open) so the user
 * never has to click "Sync" to see new sends / replies land.
 */
export const AUTO_SYNC_INTERVAL_MS = 60_000;
export const LAUNCH_WAIT_MS = 2000;
export const LAUNCH_WAIT_TRIES = 60;

/** The tab is hidden: no auto-sync, and its return reloads the page. */
const tabHidden = () => typeof document !== 'undefined' && document.hidden;

/**
 * One loop that depends on what the page shows: started one timer turn after the render whose
 * dependencies changed, stopped in the render that changed them again, or when the page is left.
 */
class PhaseLoop<D extends readonly unknown[]> {
  #deps: D | undefined;
  #stop: (() => void) | undefined;
  readonly #start: (deps: D) => (() => void) | undefined;

  constructor(start: (deps: D) => (() => void) | undefined) {
    this.#start = start;
  }

  update(deps: D): void {
    if (this.#deps && this.#deps.length === deps.length && this.#deps.every((d, i) => d === deps[i])) return;
    this.#stop?.();
    this.#stop = undefined;
    this.#deps = deps;
    const timer = setTimeout(() => {
      this.#stop = this.#start(deps);
    });
    this.#stop = () => clearTimeout(timer);
  }

  stop(): void {
    this.#stop?.();
    this.#stop = undefined;
    this.#deps = undefined;
  }
}

/** The campaign page's own loads and polling, as a controller of its PageHost. */
export class CampaignLoops implements Controller {
  #latest: CampaignLoopInput | undefined;
  #opening: ReturnType<typeof setTimeout> | undefined;
  #listening = false;
  #left = false;

  // (2) Refetch immediately when the tab becomes visible again. Message
  // generation itself runs entirely server-side (a background loop in the
  // Node process) and is completely unaffected by the browser tab's
  // visibility — but browsers throttle setInterval in hidden tabs, so the
  // poll (1) can sit stale for a while after switching back, making it
  // LOOK like generation stopped even though it never did. This closes that
  // gap by forcing a fresh fetch the moment the tab regains focus, regardless
  // of campaign status (a plain DB refetch is cheap — unlike the Instantly/
  // Unipile auto-sync (4), which deliberately stays paused while hidden).
  readonly #onVisibility = () => {
    if (!tabHidden()) this.#latest?.reload();
  };

  /** (1) Poll while messages are being generated (status === 'generating'). */
  readonly #generating = new PhaseLoop<[string | undefined]>(([status]) => {
    if (status !== 'generating') return undefined;
    const id = setInterval(() => this.#latest?.reload(), GENERATING_POLL_MS);
    return () => clearInterval(id);
  });

  /**
   * (3) Live countdown for the launch cool-down, ticking every second from
   * campaign.scheduledLaunchAt. Also periodically refetches so the page picks
   * up the transition to 'active' once the launch-cooldown worker actually
   * fires — the countdown hitting 0:00 client-side doesn't mean the server
   * has executed the launch yet (worker runs on its own ~60s tick).
   */
  readonly #coolDown = new PhaseLoop<[string | undefined, string | undefined]>(([status, scheduledAt]) => {
    if (status !== 'preparing_to_launch' || !scheduledAt) return undefined;
    const tick = () => this.#latest?.countdown(Math.max(0, new Date(scheduledAt).getTime() - Date.now()));
    tick();
    const tickId = setInterval(tick, COUNTDOWN_TICK_MS);
    const reloadId = setInterval(() => this.#latest?.reload(), COOL_DOWN_RELOAD_MS);
    return () => {
      clearInterval(tickId);
      clearInterval(reloadId);
    };
  });

  /**
   * (4) Auto-sync: keep an active campaign live without manual "Sync" clicks.
   * While the campaign is active and the tab is visible, silently pull fresh
   * stats/replies from Instantly/Unipile on an interval (no toast/spinner).
   * Skipped when the tab is hidden to avoid wasteful API calls / rate limits;
   * the server-side sync-tick-worker covers campaigns when no tab is open.
   */
  readonly #autoSync = new PhaseLoop<[string | undefined]>(([status]) => {
    if (status !== 'active') return undefined;
    const id = setInterval(() => {
      if (tabHidden()) return;
      this.#latest?.sync();
    }, AUTO_SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  });

  constructor(host: Host) {
    host.addController(this);
  }

  /** The page's loaders and phase, from the render running now. */
  use(input: CampaignLoopInput): void {
    this.#latest = input;
  }

  hostConnected(): void {
    if (this.#left || this.#opening !== undefined) return;
    this.#opening = setTimeout(() => {
      const page = this.#latest;
      if (this.#left || !page) return;
      page.reload();
      for (const load of page.openLoads) load();
      document.addEventListener('visibilitychange', this.#onVisibility);
      this.#listening = true;
      page.drafts();
    });
  }

  hostUpdated(): void {
    const page = this.#latest;
    if (this.#left || !page) return;
    this.#generating.update([page.status]);
    this.#coolDown.update([page.status, page.scheduledLaunchAt]);
    this.#autoSync.update([page.status]);
  }

  hostDisconnected(): void {
    this.#left = true;
    if (this.#opening !== undefined) clearTimeout(this.#opening);
    if (this.#listening) document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#listening = false;
    this.#generating.stop();
    this.#coolDown.stop();
    this.#autoSync.stop();
  }
}

/**
 * (5) After "Launch Now" while the server resumes the campaign. Resuming runs
 * server-side: the campaign moves through 'generating' (any step added during
 * the pause is written now) and lands on 'active'. The launch request no longer
 * waits on that generation — which is what used to time out — so the page asks
 * for the campaign every 2 s, up to 60 times, until it leaves the cool-down,
 * keeping the button disabled meanwhile; the status poll (1) then carries
 * 'generating' the rest of the way to 'active'. A request that fails is waited
 * past.
 */
export async function waitForLaunch(
  campaignId: string,
  {
    send = (url: string) => fetch(url),
    sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
  }: { send?: (url: string) => Promise<Response>; sleep?: (ms: number) => Promise<void> } = {},
): Promise<void> {
  for (let i = 0; i < LAUNCH_WAIT_TRIES; i++) {
    await sleep(LAUNCH_WAIT_MS);
    let status: string | undefined;
    try {
      const c = await (await send(`/api/campaigns/${encodeURIComponent(campaignId)}`)).json();
      status = c?.campaign?.status;
    } catch {
      /* transient — keep waiting */
    }
    if (status && status !== 'preparing_to_launch') break;
  }
}
